/**
 * The workdoc screen (`specs/M11-docs.md` §6).
 *
 * `/docs/:docId` — the segment is monday's `object_id`, the id its own links carry, so every
 * migrated `/docs/2195329046` keeps resolving (doc 00). The server accepts either id, but the
 * URL is always the object one.
 *
 * The content column is **874 px** and the toolbar carries monday's own entries; the ones this
 * product does not build are rendered **disabled with their tooltip**, never absent (F4 §3.2).
 */
import { useRef, useState } from 'react';
import { useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatMondayDate, useToast } from '@elizmonday/ui';
import { buildDocBlockTree, type DocBlockNode, type DocBlockType } from '@elizmonday/shared';
import { useTRPC, useTRPCClient } from '../../lib/trpc';
import { DocBlocks, DocEmbedProvider } from './blocks';
import { SlashMenu } from './SlashMenu';
import './docs.css';

const DISABLED_TOOLS: readonly { label: string; why: string }[] = [
  { label: 'Align', why: 'Per-block alignment is stored but not editable yet' },
  { label: 'Style', why: 'Run-level styling needs the formatting toolbar (M11 follow-up)' },
  { label: 'Apps', why: 'Doc apps are a marketplace feature' },
];

/** How many people the `@` popover offers before the visitor has typed anything. */
const MENTION_PAGE = 10;

interface MentionPerson {
  kind: string;
  id: string;
  name: string;
}

/** The block's text as the editor shows it, so a mention appends rather than replaces. */
function plainTextOf(content: unknown): string {
  if (content === null || typeof content !== 'object') return '';
  const delta = (content as { deltaFormat?: unknown }).deltaFormat;
  if (!Array.isArray(delta)) return '';
  return delta.map((op) => (typeof (op as { insert?: unknown })?.insert === 'string' ? String((op as { insert: string }).insert) : '')).join('');
}

export function DocPage() {
  const { docId } = useParams();
  return <DocScreen objectId={docId ?? ''} />;
}

export function DocScreen({ objectId }: { objectId: string }) {
  const trpc = useTRPC();
  const client = useTRPCClient();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [slash, setSlash] = useState<{ x: number; y: number; afterBlockId: string | null } | null>(null);
  const [mention, setMention] = useState<{ x: number; y: number } | null>(null);
  const [mentionQuery, setMentionQuery] = useState('');
  const [people, setPeople] = useState<MentionPerson[]>([]);
  /**
   * Which block the `@` writes into: the last one the visitor focused. A doc has no single
   * caret across blocks, and appending to the wrong paragraph is worse than appending to the
   * last one touched.
   */
  const focusedBlock = useRef<string | null>(null);

  const doc = useQuery({
    ...trpc.docs.get.queryOptions({ objectId }),
    enabled: objectId !== '',
  });

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: trpc.docs.get.queryKey({ objectId }) });
  };

  const updateBlock = useMutation({
    mutationFn: (input: { blockId: string; text?: string; checked?: boolean }) =>
      client.docs.updateBlock.mutate(input),
    onSuccess: refresh,
    onError: (error: unknown) =>
      toast(error instanceof Error ? error.message : 'The block could not be saved', { kind: 'negative' }),
  });

  const createBlock = useMutation({
    mutationFn: (input: { type: string; afterBlockId: string | null }) =>
      client.docs.createBlock.mutate({
        docId: doc.data!.doc.id,
        type: input.type,
        content: { deltaFormat: [] },
        afterBlockId: input.afterBlockId,
      }),
    onSuccess: refresh,
    onError: (error: unknown) =>
      toast(error instanceof Error ? error.message : 'The block could not be added', { kind: 'negative' }),
  });

  const rename = useMutation({
    mutationFn: (name: string) => client.docs.rename.mutate({ docId: doc.data!.doc.id, name }),
    onSuccess: refresh,
  });

  /*
   * P-185. `docs.deleteBlock` shipped with M11, was unit-tested and had **no caller** — a
   * mis-inserted block was permanent — and there was no reorder procedure at all. Both are wired
   * here, through the same `mutate()`-backed service every other doc write goes through.
   */
  const deleteBlock = useMutation({
    mutationFn: (blockId: string) => client.docs.deleteBlock.mutate({ blockId }),
    onSuccess: refresh,
    onError: (error: unknown) =>
      toast(error instanceof Error ? error.message : 'The block could not be deleted', { kind: 'negative' }),
  });

  const moveBlock = useMutation({
    mutationFn: (input: { blockId: string; afterBlockId: string | null }) => client.docs.moveBlock.mutate(input),
    onSuccess: refresh,
    onError: (error: unknown) =>
      toast(error instanceof Error ? error.message : 'The block could not be moved', { kind: 'negative' }),
  });

  if (objectId === '') return null;
  if (doc.isPending) {
    return (
      <div className="ez-doc" data-testid="doc-page">
        <p className="ez-doc__loading">Loading…</p>
      </div>
    );
  }
  if (doc.isError || !doc.data) {
    return (
      <div className="ez-doc" data-testid="doc-page">
        <p className="ez-doc__loading">This doc could not be opened.</p>
      </div>
    );
  }

  const nodes = buildDocBlockTree(doc.data.blocks);
  const lastTopLevel = nodes.at(-1)?.id ?? null;

  const pick = (type: DocBlockType): void => {
    createBlock.mutate({ type, afterBlockId: slash?.afterBlockId ?? lastTopLevel });
    setSlash(null);
  };

  /**
   * A dropped block takes the target's place: dragged **up**, it lands before the target;
   * dragged **down**, after it. That is the ordinary sortable-list reading and it needs no
   * drop-half geometry, which matters because the handle's own drop indicator is uncaptured
   * (q538).
   */
  const moveTo = (node: DocBlockNode, targetId: string): void => {
    const order = nodes.map((entry) => entry.id);
    const from = order.indexOf(node.id);
    const to = order.indexOf(targetId);
    if (from === -1 || to === -1 || from === to) return;
    const afterBlockId = from > to ? (order[to - 1] ?? null) : targetId;
    moveBlock.mutate({ blockId: node.id, afterBlockId });
  };

  /** The `@` popover's people, from the account picker every other surface uses. */
  const openMentions = (x: number, y: number): void => {
    setMention({ x, y });
    setMentionQuery('');
    void client.admin.sharing.people
      .query({ query: '', limit: MENTION_PAGE, includeTeams: false })
      .then((rows: MentionPerson[]) => setPeople(rows))
      .catch(() => setPeople([]));
  };

  /**
   * Inserting a mention writes the person's name into the block's text, which is what a reader
   * of the doc — and the exporter, and `docs.get` — then sees. It notifies **nobody**: `notify()`
   * is board-scoped end to end (it filters on board mutes and on the recipient's board read
   * access) and a workdoc has no board, so a doc-mention notification needs its own kind and its
   * own capture before it can be written. Recorded as **q538**, not guessed at.
   */
  const insertMention = (person: MentionPerson): void => {
    setMention(null);
    // The remembered block may have been deleted since it was focused, and writing to a block
    // that is gone is a 404 the visitor never asked for: fall back to the last one that exists.
    const remembered = doc.data.blocks.find((entry) => entry.id === focusedBlock.current);
    const target = remembered ?? doc.data.blocks.find((entry) => entry.id === lastTopLevel) ?? doc.data.blocks.at(-1);
    if (target === undefined) return;
    focusedBlock.current = target.id;
    const existing = plainTextOf(target.content);
    updateBlock.mutate({
      blockId: target.id,
      text: `${existing}${existing === '' || existing.endsWith(' ') ? '' : ' '}@${person.name}`,
    });
  };

  return (
    <div className="ez-doc" data-testid="doc-page" onMouseDown={() => setSlash(null)}>
      <header className="ez-doc__toolbar" data-testid="doc-toolbar">
        <button
          type="button"
          className="ez-doc__add"
          data-testid="doc-add"
          onClick={(event) => {
            event.stopPropagation();
            const box = event.currentTarget.getBoundingClientRect();
            setSlash({ x: box.left, y: box.bottom + 4, afterBlockId: lastTopLevel });
          }}
        >
          + Add
        </button>
        <span className="ez-doc__divider" />
        <span className="ez-doc__blocktype">Normal text</span>
        <span className="ez-doc__divider" />
        {/*
          P-185: `@` was rendered disabled with "Mentions inside a doc are not built yet" — the
          honest F4 §3.2 treatment, but it left the single most-used doc affordance unavailable.
        */}
        <button
          type="button"
          className="ez-doc__tool"
          data-testid="doc-mention"
          title="Mentions: write somebody into this doc"
          aria-expanded={mention !== null}
          onClick={(event) => {
            event.stopPropagation();
            const box = event.currentTarget.getBoundingClientRect();
            openMentions(box.left, box.bottom + 4);
          }}
        >
          @
        </button>
        {DISABLED_TOOLS.map((tool) => (
          <button key={tool.label} type="button" className="ez-doc__tool" disabled title={tool.why}>
            {tool.label}
          </button>
        ))}
      </header>

      <div className="ez-doc__column">
        <h1
          className="ez-doc__title"
          data-testid="doc-title"
          contentEditable
          suppressContentEditableWarning
          onBlur={(event) => {
            const next = event.currentTarget.innerText.trim();
            if (next !== '' && next !== doc.data.doc.name) rename.mutate(next);
          }}
        >
          {doc.data.doc.name}
        </h1>
        <p className="ez-doc__meta" data-testid="doc-meta">
          <span>
            Creator <strong>{doc.data.doc.createdByName ?? '—'}</strong>
          </span>
          <span>
            Created <strong>{formatMondayDate(doc.data.doc.createdAt)}</strong>
          </span>
          <span>
            Last updated <strong>{formatMondayDate(doc.data.doc.updatedAt)}</strong>
          </span>
        </p>

        <div
          className="ez-doc__body"
          data-testid="doc-body"
          onFocusCapture={(event) => {
            const row = (event.target as HTMLElement).closest('[data-block-id]');
            focusedBlock.current = row?.getAttribute('data-block-id') ?? focusedBlock.current;
          }}
        >
          <DocEmbedProvider resolve={(widgetId) => client.docs.boardEmbed.query({ widgetId })}>
          <DocBlocks
            nodes={nodes}
            onToggleCheck={(node: DocBlockNode, checked: boolean) =>
              updateBlock.mutate({ blockId: node.id, checked })
            }
            onEditText={(node: DocBlockNode, text: string) =>
              updateBlock.mutate({ blockId: node.id, text })
            }
            onMove={moveTo}
            onDelete={(node: DocBlockNode) => deleteBlock.mutate(node.id)}
          />
          </DocEmbedProvider>
          {nodes.length === 0 ? (
            <p className="ez-doc__empty" data-testid="doc-empty">
              This doc is empty. Use <strong>+ Add</strong> to write the first block.
            </p>
          ) : null}
        </div>
      </div>

      {slash === null ? null : (
        <SlashMenu x={slash.x} y={slash.y} onPick={pick} onClose={() => setSlash(null)} />
      )}

      {mention === null ? null : (
        <div
          className="ez-doc-mention-popup"
          role="listbox"
          aria-label="Mention somebody"
          data-testid="doc-mention-popup"
          style={{ insetInlineStart: mention.x, insetBlockStart: mention.y }}
          onMouseDown={(event) => event.stopPropagation()}
        >
          <input
            type="text"
            autoFocus
            placeholder="Search people"
            aria-label="Search people to mention"
            data-testid="doc-mention-search"
            value={mentionQuery}
            onChange={(event) => setMentionQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Escape') setMention(null);
            }}
          />
          {people
            .filter((person) => person.name.toLowerCase().includes(mentionQuery.trim().toLowerCase()))
            .map((person) => (
              <button
                key={`${person.kind}-${person.id}`}
                type="button"
                role="option"
                aria-selected={false}
                data-testid={`doc-mention-${person.id}`}
                onClick={() => insertMention(person)}
              >
                {person.name}
              </button>
            ))}
          {people.length === 0 ? (
            <p className="ez-doc-mention-popup__empty" data-testid="doc-mention-empty">
              Nobody to mention.
            </p>
          ) : null}
        </div>
      )}
    </div>
  );
}

/** `/docs` — the list. monday has no such screen; ours needs one to reach the 20 imported. */
export function DocListPage() {
  const trpc = useTRPC();
  const docs = useQuery({ ...trpc.docs.list.queryOptions({ limit: 50, page: 1 }), staleTime: 30_000 });

  return (
    <div className="ez-doc-list" data-testid="doc-list">
      <h1>Docs</h1>
      {docs.isPending ? (
        <p>Loading…</p>
      ) : (docs.data ?? []).length === 0 ? (
        <p data-testid="doc-list-empty">No docs yet.</p>
      ) : (
        <ul>
          {(docs.data ?? []).map((row) => (
            <li key={row.id}>
              <a href={`/docs/${row.objectId ?? row.id}`} data-testid={`doc-link-${row.id}`}>
                {row.name}
              </a>
              <span>{formatMondayDate(row.updatedAt)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
